import { useState, useEffect, useCallback, useRef } from 'react';

interface GeolocationState {
  position: { latitude: number; longitude: number } | null;
  error: GeolocationPositionError | null;
  loading: boolean;
  permissionState: 'prompt' | 'granted' | 'denied' | 'unknown';
  lastUpdated: number | null;
}

interface UseGeolocationOptions {
  enableHighAccuracy?: boolean;   // Default: false
  cacheTTL?: number;              // Default: 15 minutes (900000ms)
  updateInterval?: number;        // Default: 5 minutes (300000ms)
}

export function useGeolocation(options: UseGeolocationOptions = {}) {
  const {
    enableHighAccuracy = false,
    cacheTTL = 15 * 60 * 1000,
    updateInterval = 5 * 60 * 1000,
  } = options;

  const [state, setState] = useState<GeolocationState>({
    position: null,
    error: null,
    loading: false,
    permissionState: 'unknown',
    lastUpdated: null,
  });

  // Use ref to avoid stale closures in interval callback
  const stateRef = useRef(state);
  stateRef.current = state;

  // Check cached position on mount
  useEffect(() => {
    try {
      const cached = localStorage.getItem('ownyou_geolocation');
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Date.now() - parsed.timestamp < cacheTTL) {
          setState(prev => ({
            ...prev,
            position: parsed.position,
            lastUpdated: parsed.timestamp,
          }));
        }
      }
    } catch {
      // Ignore localStorage errors
    }
  }, [cacheTTL]);

  // Check permission state
  useEffect(() => {
    if ('permissions' in navigator) {
      navigator.permissions.query({ name: 'geolocation' as PermissionName }).then(result => {
        setState(prev => ({ ...prev, permissionState: result.state as GeolocationState['permissionState'] }));
        result.onchange = () => {
          setState(prev => ({ ...prev, permissionState: result.state as GeolocationState['permissionState'] }));
        };
      }).catch(() => {
        // permissions API not available
      });
    }
  }, []);

  const getPosition = useCallback((): Promise<GeolocationPosition> => {
    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy,
        timeout: 10000,
        maximumAge: cacheTTL,
      });
    });
  }, [enableHighAccuracy, cacheTTL]);

  const requestPermission = useCallback(async () => {
    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const position = await getPosition();
      const pos = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
      };

      // Cache position
      try {
        localStorage.setItem('ownyou_geolocation', JSON.stringify({
          position: pos,
          timestamp: Date.now(),
        }));
      } catch {
        // Ignore localStorage errors
      }

      setState({
        position: pos,
        error: null,
        loading: false,
        permissionState: 'granted',
        lastUpdated: Date.now(),
      });
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: error as GeolocationPositionError,
        loading: false,
        permissionState: (error as GeolocationPositionError).code === 1 ? 'denied' : prev.permissionState,
      }));
    }
  }, [getPosition]);

  const refresh = useCallback(async () => {
    if (stateRef.current.permissionState === 'granted') {
      await requestPermission();
    }
  }, [requestPermission]);

  // Periodic update when permission granted
  useEffect(() => {
    if (state.permissionState !== 'granted') return;

    const interval = setInterval(refresh, updateInterval);
    return () => clearInterval(interval);
  }, [state.permissionState, updateInterval, refresh]);

  return { ...state, requestPermission, refresh };
}
